import {
  CaretRightIcon,
  FileIcon,
  FolderIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import type { AnalysisReport, PathAnalysis } from '../types';
import { useI18n } from '../i18n';

export type FileStatusFilter = 'all' | 'ignored' | 'tracked' | 'warnings';

interface FilesPanelProps {
  readonly report: AnalysisReport | null;
  readonly selectedPath: string | null;
  readonly selectedRuleId: string | null;
  readonly onSelectPath: (path: string) => void;
}

const INITIAL_RENDER_LIMIT = 1000;

function parentPaths(path: string): string[] {
  const parts = path.replace(/\/$/, '').split('/');
  const parents: string[] = [];
  for (let index = 1; index < parts.length; index += 1) {
    parents.push(`${parts.slice(0, index).join('/')}/`);
  }
  return parents;
}

function pathDepth(path: string): number {
  return Math.max(0, path.replace(/\/$/, '').split('/').length - 1);
}

function matchesStatus(path: PathAnalysis, filter: FileStatusFilter): boolean {
  if (filter === 'ignored') return path.ignored;
  if (filter === 'tracked') return !path.ignored;
  if (filter === 'warnings') return path.warnings.length > 0;
  return true;
}

export function FilesPanel({
  report,
  selectedPath,
  selectedRuleId,
  onSelectPath,
}: FilesPanelProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FileStatusFilter>('all');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [renderLimit, setRenderLimit] = useState(INITIAL_RENDER_LIMIT);

  function handleTreeKeyDown(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    entry: PathAnalysis,
  ): void {
    const tree = event.currentTarget.closest('[role="tree"]');
    if (tree === null) {
      return;
    }
    const items = [...tree.querySelectorAll<HTMLButtonElement>('[role="treeitem"]')];
    const index = items.indexOf(event.currentTarget);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[Math.min(index + 1, items.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[Math.max(index - 1, 0)]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      items[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      items.at(-1)?.focus();
    } else if (event.key === 'ArrowRight' && entry.kind === 'directory') {
      event.preventDefault();
      if (collapsed.has(entry.path)) {
        toggleDirectory(entry.path);
      } else {
        items[index + 1]?.focus();
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (entry.kind === 'directory' && !collapsed.has(entry.path)) {
        toggleDirectory(entry.path);
      } else {
        const parent = parentPaths(entry.path).at(-1);
        items.find((item) => item.dataset.path === parent)?.focus();
      }
    }
  }

  const filteredPaths = useMemo(() => {
    if (!report) return [];
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const selectedRule = selectedRuleId
      ? report.rules.find((rule) => rule.id === selectedRuleId)
      : undefined;
    const affected = selectedRule ? new Set(selectedRule.affectedPaths) : null;
    const included = new Set<string>();

    for (const path of report.paths) {
      if (path.kind === 'directory') continue;
      if (normalizedQuery && !path.path.toLocaleLowerCase().includes(normalizedQuery)) continue;
      if (!matchesStatus(path, filter)) continue;
      if (affected && !affected.has(path.path)) continue;
      included.add(path.path);
      for (const parent of parentPaths(path.path)) included.add(parent);
    }

    return report.paths.filter((path) => included.has(path.path));
  }, [filter, query, report, selectedRuleId]);

  const visiblePaths = useMemo(
    () =>
      filteredPaths.filter((entry) => {
        const parents = parentPaths(entry.path);
        return !parents.some((parent) => collapsed.has(parent));
      }),
    [collapsed, filteredPaths],
  );

  const renderedPaths = visiblePaths.slice(0, renderLimit);
  const hiddenCount = Math.max(0, visiblePaths.length - renderedPaths.length);
  const counts = report?.summary;

  function toggleDirectory(path: string): void {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  return (
    <section id="panel-files" className="panel files-panel" aria-labelledby="files-heading">
      <div className="panel-heading files-heading">
        <div>
          <p className="panel-kicker">{t('panels.files.kicker')}</p>
          <h2 id="files-heading">{t('panels.files.title')}</h2>
        </div>
        <div className="file-totals" aria-label={t('panels.files.summaryLabel')}>
          <strong>{counts?.pathCount ?? 0}</strong>
          <span>{t('panels.files.paths')}</span>
          <strong className="ignored-text">{counts?.ignoredCount ?? 0}</strong>
          <span>{t('panels.files.ignored')}</span>
        </div>
      </div>

      <label className="search-field">
        <span className="visually-hidden">{t('panels.files.searchLabel')}</span>
        <MagnifyingGlassIcon aria-hidden="true" />
        <input
          type="search"
          value={query}
          placeholder={t('panels.files.searchPlaceholder')}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setRenderLimit(INITIAL_RENDER_LIMIT);
          }}
        />
      </label>

      <div className="filter-tabs" role="group" aria-label={t('panels.files.filters')}>
        {(['all', 'ignored', 'tracked', 'warnings'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={filter === value ? 'is-active' : ''}
            aria-pressed={filter === value}
            onClick={() => {
              setFilter(value);
              setRenderLimit(INITIAL_RENDER_LIMIT);
            }}
          >
            {t(`panels.files.filter.${value}`)}
          </button>
        ))}
      </div>

      <div className="file-tree" role="tree" aria-label={t('panels.files.treeLabel')}>
        {renderedPaths.map((entry) => {
          const isDirectory = entry.kind === 'directory';
          const isCollapsed = collapsed.has(entry.path);
          const depthStyle = { '--depth': pathDepth(entry.path) } as CSSProperties & {
            '--depth': number;
          };
          return (
            <button
              type="button"
              role="treeitem"
              key={entry.path}
              style={depthStyle}
              className={`file-row${selectedPath === entry.path ? ' is-selected' : ''}`}
              data-path={entry.path}
              aria-level={pathDepth(entry.path) + 1}
              aria-selected={selectedPath === entry.path}
              aria-expanded={isDirectory ? !isCollapsed : undefined}
              onKeyDown={(event) => {
                handleTreeKeyDown(event, entry);
              }}
              onClick={() => {
                if (isDirectory) toggleDirectory(entry.path);
                onSelectPath(entry.path);
              }}
            >
              <span className="tree-caret" aria-hidden="true">
                {isDirectory ? <CaretRightIcon className={isCollapsed ? '' : 'is-open'} /> : null}
              </span>
              <span className="tree-icon" aria-hidden="true">
                {isDirectory ? isCollapsed ? <FolderIcon /> : <FolderOpenIcon /> : <FileIcon />}
              </span>
              <code title={entry.path}>{entry.path.replace(/\/$/, '').split('/').at(-1)}</code>
              {entry.warnings.length > 0 ? (
                <WarningIcon className="warning-icon" aria-label={t('panels.files.hasWarnings')} />
              ) : null}
              <span className={`path-state ${entry.ignored ? 'is-ignored' : 'is-tracked'}`}>
                {entry.ignored ? t('states.ignored') : t('states.tracked')}
              </span>
            </button>
          );
        })}
        {renderedPaths.length === 0 ? (
          <div className="empty-state">
            <FolderOpenIcon aria-hidden="true" />
            <h3>{t('panels.files.empty')}</h3>
            <p>{t('panels.files.emptyHelp')}</p>
          </div>
        ) : null}
        {hiddenCount > 0 ? (
          <button
            className="load-more"
            type="button"
            onClick={() => {
              setRenderLimit((limit) => limit + INITIAL_RENDER_LIMIT);
            }}
          >
            {t('panels.files.showMore', { count: Math.min(hiddenCount, INITIAL_RENDER_LIMIT) })}
          </button>
        ) : null}
      </div>
    </section>
  );
}
